import { useEffect, useMemo, useState } from "react";
import { crmSupabase } from "../../lib/crm-client";
import RefreshModal from "./RefreshModal";
import {
  getOrCreateDraftBatch,
  clearBatchSends,
  queueSends,
  lockAndSendBatch,
  markBatchSent,
  markSendAsSent,
  markSendAsFailed,
  sendEmailViaEdge,
  scheduleBatch,
  type CurrentBatch,
} from "../../lib/crm-mutations";

type Touchpoint = {
  id: string;
  number: string;
  title: string;
  status: string;
  channel: "email" | "whatsapp";
};

type LeadRow = {
  id: string;
  full_name: string;
  is_estrella: boolean;
  is_test: boolean;
  status: string;
  category: { name: string; color_hex: string } | null;
  primary_email: string | null;
  received_tp_ids: Set<string>;
  last_received_tp_id: string | null;
  last_received_at: string | null;
  last_response_at: string | null;
  /** Number of days since they replied, or null if no reply. Used for auto-snooze. */
  days_since_response: number | null;
};

const AUTO_SNOOZE_DAYS = 10; // si lead a répondu dans les N derniers jours, snooze par défaut

const VISIBLE_TPS = 10; // boutons visibles par défaut dans la matrice

export default function CrmBatches() {
  const [batch, setBatch] = useState<CurrentBatch | null>(null);
  const [touchpoints, setTouchpoints] = useState<Touchpoint[]>([]);
  const [leads, setLeads] = useState<LeadRow[]>([]);
  const [selections, setSelections] = useState<Record<string, string>>({}); // lead_id -> tp_id
  const [snoozed, setSnoozed] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [log, setLog] = useState<string[]>([]);
  const [tpOffset, setTpOffset] = useState(0);
  const [scheduleAt, setScheduleAt] = useState<string>("");
  const [refreshOpen, setRefreshOpen] = useState(false);

  useEffect(() => {
    (async () => {
      if (!crmSupabase) {
        setLoading(false);
        return;
      }
      const { data: tps } = await crmSupabase
        .from("touchpoint")
        .select("id,number,title,status,channel")
        .eq("status", "ready")
        .order("number", { ascending: true });
      const tpList = (tps as Touchpoint[]) ?? [];
      setTouchpoints(tpList);

      const b = await getOrCreateDraftBatch(tpList[0]?.id ?? null);
      setBatch(b);

      const { data: ll } = await crmSupabase
        .from("lead")
        .select(
          "id,full_name,is_estrella,is_test,status,last_response_at,category:category_id(name,color_hex),identities:lead_identity(kind,value,is_primary)",
        )
        .in("status", ["active", "responded"]);

      const { data: sends } = await crmSupabase
        .from("touchpoint_send")
        .select("lead_id,touchpoint_id,sent_at")
        .in("status", ["sent", "sent_manually"]);

      const receivedByLead: Map<string, Set<string>> = new Map();
      const lastByLead: Map<string, { tpId: string; sentAt: string }> = new Map();
      for (const s of (sends as any[]) ?? []) {
        if (!receivedByLead.has(s.lead_id))
          receivedByLead.set(s.lead_id, new Set());
        receivedByLead.get(s.lead_id)!.add(s.touchpoint_id);
        const cur = lastByLead.get(s.lead_id);
        if (s.sent_at && (!cur || s.sent_at > cur.sentAt)) {
          lastByLead.set(s.lead_id, { tpId: s.touchpoint_id, sentAt: s.sent_at });
        }
      }

      const now = Date.now();
      const autoSnoozedIds = new Set<string>();
      const rows: LeadRow[] = ((ll as any[]) ?? []).map((l) => {
        const identities = l.identities ?? [];
        const primary =
          identities.find((i: any) => i.kind === "email" && i.is_primary)
            ?.value ?? null;
        const last = lastByLead.get(l.id);
        let daysSinceResponse: number | null = null;
        if (l.last_response_at) {
          daysSinceResponse = Math.floor(
            (now - new Date(l.last_response_at).getTime()) / 86400_000,
          );
          if (daysSinceResponse <= AUTO_SNOOZE_DAYS) {
            autoSnoozedIds.add(l.id);
          }
        }
        return {
          id: l.id,
          full_name: l.full_name,
          is_estrella: l.is_estrella,
          is_test: l.is_test,
          status: l.status,
          category: l.category,
          primary_email: primary,
          received_tp_ids: receivedByLead.get(l.id) ?? new Set(),
          last_received_tp_id: last?.tpId ?? null,
          last_received_at: last?.sentAt ?? null,
          last_response_at: l.last_response_at ?? null,
          days_since_response: daysSinceResponse,
        };
      });

      // Sort: Estrellas first, then real leads, then test users
      rows.sort((a, b) => {
        if (a.is_test !== b.is_test) return a.is_test ? 1 : -1;
        if (a.is_estrella !== b.is_estrella) return a.is_estrella ? -1 : 1;
        return a.full_name.localeCompare(b.full_name);
      });
      setLeads(rows);
      setSnoozed(autoSnoozedIds);

      // Default selection : first TP not yet received
      const sel: Record<string, string> = {};
      for (const row of rows) {
        const next = tpList.find((t) => !row.received_tp_ids.has(t.id));
        if (next) sel[row.id] = next.id;
      }
      setSelections(sel);

      setLoading(false);
    })().catch((e) => {
      console.error(e);
      setLoading(false);
    });
  }, []);

  const toggleSnooze = (leadId: string) => {
    setSnoozed((s) => {
      const next = new Set(s);
      if (next.has(leadId)) next.delete(leadId);
      else next.add(leadId);
      return next;
    });
  };

  const setSelection = (leadId: string, tpId: string) => {
    setSelections((s) => ({ ...s, [leadId]: tpId }));
  };

  const visibleTps = useMemo(
    () => touchpoints.slice(tpOffset, tpOffset + VISIBLE_TPS),
    [touchpoints, tpOffset],
  );

  const activeRows = useMemo(
    () =>
      leads.filter(
        (l) => !snoozed.has(l.id) && selections[l.id] && l.primary_email,
      ),
    [leads, snoozed, selections],
  );

  const snoozedRows = useMemo(
    () => leads.filter((l) => snoozed.has(l.id)),
    [leads, snoozed],
  );

  const noEmailRows = useMemo(
    () =>
      leads.filter(
        (l) => !snoozed.has(l.id) && selections[l.id] && !l.primary_email,
      ),
    [leads, snoozed, selections],
  );

  const handleSchedule = async () => {
    if (!batch || activeRows.length === 0 || !scheduleAt) return;
    const localDate = new Date(scheduleAt);
    if (Number.isNaN(localDate.getTime())) {
      alert("Date invalide");
      return;
    }
    if (localDate.getTime() < Date.now() - 60_000) {
      alert("La date doit être dans le futur (au moins ~maintenant).");
      return;
    }
    const ok = confirm(
      `Planifier l'envoi de ${activeRows.length} mail(s) le ${localDate.toLocaleString("fr-FR")} ?\n\n` +
        `Le batch passera en status='scheduled' et sera execute automatiquement a cette date (scheduler dans le pipeline a venir). En attendant, tu peux deverouiller manuellement.`,
    );
    if (!ok) return;
    setSending(true);
    setLog([
      `▶ Schedule du batch ${batch.id.slice(0, 8)} pour ${localDate.toISOString()}`,
    ]);
    try {
      await clearBatchSends(batch.id);
      await queueSends(
        batch.id,
        activeRows.map((r) => ({
          lead_id: r.id,
          touchpoint_id: selections[r.id],
          channel:
            touchpoints.find((t) => t.id === selections[r.id])?.channel ??
            "email",
        })),
      );
      await scheduleBatch(batch.id, localDate.toISOString());
      setLog((l) => [
        ...l,
        `✓ Batch planifie. ${activeRows.length} envois prets, attendront ${localDate.toLocaleString("fr-FR")}.`,
      ]);
    } catch (e: any) {
      console.error(e);
      setLog((l) => [...l, `❌ erreur : ${e?.message ?? e}`]);
    } finally {
      setSending(false);
    }
  };

  const handleLockAndSend = async () => {
    if (!batch || activeRows.length === 0) return;
    const ok = confirm(
      `Lock & envoi de ${activeRows.length} mail(s) via Resend.\n\n⚠ Safelist active : seuls les destinataires qui matchent jeremy+N@llyll.com seront effectivement envoyés. Les autres seront "skipped" (loggés mais pas envoyés).\n\nContinuer ?`,
    );
    if (!ok) return;
    setSending(true);
    setLog([`▶ Lock du batch ${batch.id.slice(0, 8)}`]);
    try {
      await clearBatchSends(batch.id);
      await queueSends(
        batch.id,
        activeRows.map((r) => ({
          lead_id: r.id,
          touchpoint_id: selections[r.id],
          channel:
            touchpoints.find((t) => t.id === selections[r.id])?.channel ??
            "email",
        })),
      );
      await lockAndSendBatch(batch.id);

      // Fetch the freshly-queued sends with their full lead + tp info
      const { data: queued } = await crmSupabase!
        .from("touchpoint_send")
        .select(
          "id,lead_id,touchpoint_id,channel,lead:lead_id(full_name,identities:lead_identity(kind,value,is_primary)),touchpoint:touchpoint_id(number,title,body_text,body_html,cta_label,video_url)",
        )
        .eq("batch_id", batch.id)
        .eq("status", "pending");

      let sentCount = 0;
      let skippedCount = 0;
      let failedCount = 0;

      for (const s of (queued as any[]) ?? []) {
        const email = (s.lead?.identities ?? []).find(
          (i: any) => i.kind === "email" && i.is_primary,
        )?.value;
        if (!email) {
          await markSendAsFailed(s.id, "no primary email");
          failedCount += 1;
          setLog((l) => [...l, `❌ ${s.lead?.full_name} — no email`]);
          continue;
        }
        const firstName = (s.lead?.full_name ?? "").split(" ")[0];
        const subject = (s.touchpoint?.title ?? "").replace(
          /\{prénom\}/g,
          firstName,
        );
        let bodyText = (s.touchpoint?.body_text ?? "").replace(
          /\{prénom\}/g,
          firstName,
        );
        if (s.touchpoint?.video_url) {
          bodyText += `\n\n${s.touchpoint.cta_label ?? "Vidéo"} → ${s.touchpoint.video_url}`;
        }
        const res = await sendEmailViaEdge({
          to: email,
          subject,
          body_text: bodyText,
        });
        if (res.ok && (res as any).external_id) {
          await markSendAsSent(s.id, (res as any).external_id);
          sentCount += 1;
          setLog((l) => [...l, `✓ ${s.lead?.full_name} (${email})`]);
        } else if (res.ok && (res as any).skipped) {
          await markSendAsFailed(
            s.id,
            `skipped: ${(res as any).skip_reason}`,
          );
          skippedCount += 1;
          setLog((l) => [
            ...l,
            `⊘ ${s.lead?.full_name} skipped (safelist)`,
          ]);
        } else {
          await markSendAsFailed(s.id, res.error ?? "unknown");
          failedCount += 1;
          setLog((l) => [...l, `❌ ${s.lead?.full_name} — ${res.error}`]);
        }
      }

      await markBatchSent(batch.id);
      setLog((l) => [
        ...l,
        `─── ${sentCount} envoyés · ${skippedCount} skipped · ${failedCount} failed`,
      ]);
    } catch (e: any) {
      console.error(e);
      setLog((l) => [...l, `❌ erreur globale : ${e?.message ?? e}`]);
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <main className="px-10 py-8 max-w-[1400px] mx-auto font-mono text-sm text-concrete">
        Chargement…
      </main>
    );
  }

  if (!batch) {
    return (
      <main className="px-10 py-8 max-w-[1400px] mx-auto">
        <p className="font-mono text-sm text-concrete">
          Aucun batch en préparation et impossible d'en créer un. Vérifie qu'il y
          a au moins un touchpoint en status 'ready'.
        </p>
      </main>
    );
  }

  return (
    <main className="px-10 py-8 max-w-[1400px] mx-auto pb-32">
      <header className="mb-6 flex items-baseline justify-between">
        <div>
          <h2 className="font-sans text-4xl font-bold tracking-tight text-charcoal">
            Batch en préparation
          </h2>
          <p className="font-mono text-xs uppercase text-concrete mt-1">
            id {batch.id.slice(0, 8)} · {batch.status} · vue unifiée tous TPs
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className="font-mono text-xs uppercase text-concrete">
            {activeRows.length} envois · {snoozedRows.length} snoozés ·{" "}
            {noEmailRows.length} sans email
          </span>
          <button
            onClick={() => setRefreshOpen(true)}
            className="px-3 py-1.5 bg-charcoal text-ivory rounded text-xs font-mono uppercase hover:bg-black"
            title="Lancer un refresh depuis Claude Code local"
          >
            🔄 Rafraîchir
          </button>
        </div>
      </header>
      <RefreshModal
        open={refreshOpen}
        onClose={() => setRefreshOpen(false)}
        onApplied={() => window.location.reload()}
      />

      <div className="bg-white border border-stone rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-stone font-mono text-[10px] uppercase text-charcoal">
            <tr>
              <th className="w-10 px-2 py-2"></th>
              <th className="text-left px-3 py-2">Lead</th>
              <th className="text-left px-3 py-2">Catégorie</th>
              <th className="text-left px-3 py-2">
                <div className="flex items-center gap-2">
                  <span>
                    Touchpoints (clic = sélectionner) · TP{tpOffset + 1}–TP
                    {Math.min(tpOffset + VISIBLE_TPS, touchpoints.length)} sur{" "}
                    {touchpoints.length}
                  </span>
                  {touchpoints.length > VISIBLE_TPS && (
                    <span className="inline-flex gap-1 ml-1">
                      <button
                        onClick={() => setTpOffset(Math.max(0, tpOffset - 1))}
                        disabled={tpOffset === 0}
                        className="w-5 h-5 inline-flex items-center justify-center border border-charcoal bg-white rounded text-charcoal disabled:opacity-30"
                        title="Décaler à gauche"
                      >
                        ◀
                      </button>
                      <button
                        onClick={() =>
                          setTpOffset(
                            Math.min(
                              touchpoints.length - VISIBLE_TPS,
                              tpOffset + 1,
                            ),
                          )
                        }
                        disabled={tpOffset + VISIBLE_TPS >= touchpoints.length}
                        className="w-5 h-5 inline-flex items-center justify-center border border-charcoal bg-white rounded text-charcoal disabled:opacity-30"
                        title="Décaler à droite"
                      >
                        ▶
                      </button>
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3 mt-1 normal-case text-[9px] text-concrete">
                  <span className="flex items-center gap-1">
                    <span className="inline-block w-3 h-3 rounded border border-charcoal bg-charcoal" />
                    dernier reçu
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="inline-block w-3 h-3 rounded border border-stone bg-stone" />
                    déjà reçu
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="inline-block w-3 h-3 rounded border-2 border-black bg-lemonade" />
                    sélectionné (sera envoyé)
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="inline-block w-3 h-3 rounded border border-stone bg-white" />
                    disponible
                  </span>
                </div>
              </th>
              <th className="text-right px-3 py-2 w-32"></th>
            </tr>
          </thead>
          <tbody>
            {leads.map((row) => {
              const isSnoozed = snoozed.has(row.id);
              const eligibleTps = touchpoints.filter(
                (t) => !row.received_tp_ids.has(t.id),
              );
              return (
                <tr
                  key={row.id}
                  className={`border-t border-stone ${
                    isSnoozed
                      ? "bg-orange-50 opacity-70"
                      : row.is_test
                        ? "border-t-2 border-dashed border-stone"
                        : ""
                  }`}
                >
                  <td className="px-2 py-2 text-center">
                    {row.is_estrella && <span className="text-lemonade">★</span>}
                  </td>
                  <td className="px-3 py-2">
                    <div className="font-semibold text-charcoal">
                      {row.full_name}
                    </div>
                    {row.primary_email ? (
                      <div className="font-mono text-[10px] text-ash">
                        {row.primary_email}
                      </div>
                    ) : (
                      <div className="font-mono text-[10px] text-red-600">
                        ⚠ pas d'email
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {row.category && (
                      <span
                        className="font-mono text-[9px] uppercase px-1.5 py-0.5 rounded text-white"
                        style={{ backgroundColor: row.category.color_hex }}
                      >
                        {row.category.name}
                      </span>
                    )}
                    {row.is_test && (
                      <span className="ml-1 font-mono text-[9px] text-ash">
                        🧪 test
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {isSnoozed ? (
                      <div className="flex flex-col gap-0.5">
                        <span className="font-mono text-[11px] text-orange-700">
                          {row.days_since_response !== null &&
                          row.days_since_response <= AUTO_SNOOZE_DAYS
                            ? `↩ a répondu il y a ${row.days_since_response}j — auto-snoozé`
                            : "⏸ snoozé manuellement (→ batch suivant)"}
                        </span>
                        <span className="font-mono text-[9px] text-ash italic">
                          Géré dans /crm/today, pas de mail auto. Force-unsnooze à droite si besoin.
                        </span>
                      </div>
                    ) : eligibleTps.length === 0 ? (
                      <span className="font-mono text-[11px] text-ash">
                        Tous les TPs déjà reçus
                      </span>
                    ) : (
                      <TpButtonRow
                        tps={visibleTps}
                        receivedIds={row.received_tp_ids}
                        lastReceivedId={row.last_received_tp_id}
                        activeId={selections[row.id] ?? null}
                        onSelect={(id) => setSelection(row.id, id)}
                      />
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button
                      onClick={() => toggleSnooze(row.id)}
                      className="px-2 py-1 rounded border border-stone bg-white text-charcoal text-[10px] font-mono uppercase hover:border-charcoal"
                    >
                      {isSnoozed ? "↻ Ré-activer" : "⏸ Snooze"}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {log.length > 0 && (
        <div className="mt-6 bg-charcoal text-ivory rounded-lg p-4 font-mono text-xs max-h-60 overflow-y-auto">
          {log.map((line, i) => (
            <div key={i}>{line}</div>
          ))}
        </div>
      )}

      {/* Send-bar fixed at bottom */}
      <div className="fixed bottom-0 left-0 right-0 bg-charcoal text-ivory px-10 py-4 flex items-center justify-between gap-4 shadow-lg">
        <div className="text-sm">
          <strong>{activeRows.length} envois</strong> programmés ·{" "}
          {snoozedRows.length} snoozés · safelist test active
          (jeremy+N@llyll.com)
        </div>
        <div className="flex items-center gap-2">
          <label className="font-mono text-[10px] uppercase text-stone">
            Planifier :
            <input
              type="datetime-local"
              value={scheduleAt}
              onChange={(e) => setScheduleAt(e.target.value)}
              className="ml-2 px-2 py-1 rounded bg-charcoal text-ivory border border-stone font-mono text-xs focus:outline-none focus:border-lemonade"
            />
          </label>
          {scheduleAt ? (
            <button
              onClick={handleSchedule}
              disabled={sending || activeRows.length === 0}
              className="px-4 py-2 bg-stone text-charcoal font-bold rounded hover:bg-ivory disabled:opacity-40 disabled:cursor-not-allowed"
              title="Le batch sera mis en file d attente, execute automatiquement a la date choisie"
            >
              {sending ? "…" : "🕒 Lock & Planifier"}
            </button>
          ) : (
            <button
              onClick={handleLockAndSend}
              disabled={sending || activeRows.length === 0}
              className="px-5 py-2 bg-lemonade text-charcoal font-bold rounded hover:bg-yellow-300 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {sending ? "Envoi en cours…" : "🔒 Lock & Envoyer maintenant"}
            </button>
          )}
        </div>
      </div>
    </main>
  );
}

// ----------------------------------------------------------------------------
// TpButtonRow — rangée de boutons par TP avec 4 états visuels :
//   neutral (disponible)
//   active (sélectionné, sera envoyé après lock)
//   received (déjà reçu)
//   last-received (le plus récent envoi à ce lead, encore plus marqué)
// ----------------------------------------------------------------------------

function TpButtonRow({
  tps,
  receivedIds,
  lastReceivedId,
  activeId,
  onSelect,
}: {
  tps: Touchpoint[];
  receivedIds: Set<string>;
  lastReceivedId: string | null;
  activeId: string | null;
  onSelect: (tpId: string) => void;
}) {
  return (
    <div className="flex gap-1 flex-wrap">
      {tps.map((t) => {
        const isReceived = receivedIds.has(t.id);
        const isLast = lastReceivedId === t.id;
        const isActive = activeId === t.id;
        let cls =
          "min-w-[42px] h-8 px-2 rounded font-mono text-[10px] font-bold tracking-wide border-[1.5px] transition cursor-pointer";
        let tooltip = `TP${t.number} · ${t.title}`;
        if (isLast) {
          cls += " bg-charcoal text-ivory border-charcoal cursor-not-allowed opacity-95";
          tooltip += " · DERNIER REÇU (récemment)";
        } else if (isReceived) {
          cls += " bg-stone text-ash border-stone line-through cursor-not-allowed opacity-70";
          tooltip += " · déjà reçu";
        } else if (isActive) {
          cls +=
            " bg-lemonade text-black border-2 border-black -translate-y-px shadow-[0_2px_0_black]";
          tooltip += " · SÉLECTIONNÉ (sera envoyé)";
        } else {
          cls += " bg-white text-charcoal border-stone hover:border-charcoal hover:bg-ivory";
          tooltip += " · disponible (cliquer pour sélectionner)";
        }
        return (
          <button
            key={t.id}
            aria-label={tooltip}
            title={tooltip}
            disabled={isReceived}
            onClick={() => !isReceived && onSelect(t.id)}
            className={cls}
          >
            {isReceived && !isLast && "✓ "}
            {isLast && "● "}
            {t.number}
          </button>
        );
      })}
    </div>
  );
}
